/**
 * Composable to convert relative URLs to absolute URLs using the ORIGIN from runtime config.
 * Useful for og:image and other meta tags that require absolute URLs.
 */
export function useAbsoluteUrl() {
	const { ORIGIN } = useRuntimeConfig().public;

	/**
	 * Converts a relative path to an absolute URL.
	 * If the path is already absolute (starts with http or www), returns it as-is.
	 *
	 * @param path - The relative or absolute path
	 * @returns The absolute URL
	 */
	function toAbsoluteUrl(path: string | undefined | null): string {
		if (!path) return '';

		// Already absolute
		if (path.startsWith('http') || path.startsWith('www')) {
			return path;
		}

		// Ensure path starts with /
		const normalizedPath = path.startsWith('/') ? path : `/${path}`;
		return `${ORIGIN}${normalizedPath}`;
	}

	/**
	 * Creates a computed ref that returns an absolute URL.
	 * Useful for reactive og:image values.
	 *
	 * @param pathGetter - A function that returns the path
	 * @returns A computed ref with the absolute URL
	 */
	function computedAbsoluteUrl(pathGetter: () => string | undefined | null) {
		return computed(() => toAbsoluteUrl(pathGetter()));
	}

	return {
		toAbsoluteUrl,
		computedAbsoluteUrl,
		origin: ORIGIN as string
	};
}
